// Lean banner primitive. Default: white background, colored icon + thin
// colored border, neutral foreground text. The lean philosophy: clean by
// default, color appears as information (icon + border), not decoration.
//
// For surfaces where a colored background is the *intended* signal (e.g.
// system-wide impersonation banner, "Connected" success-state card), do NOT
// use this — those exceptions stay hand-coded.

import { AlertCircle, CheckCircle2, Info, type LucideIcon } from 'lucide-react';
import { type ReactNode } from 'react';

import { cn } from '@/lib/utils';

type Variant = 'warn' | 'error' | 'success' | 'info';
type Size = 'default' | 'compact';

interface BannerProps {
  variant: Variant;
  size?: Size;
  /** Override the variant's default icon (e.g. Info instead of AlertCircle on a warn banner). */
  icon?: LucideIcon;
  className?: string;
  children: ReactNode;
}

const VARIANT_STYLES: Record<
  Variant,
  { border: string; iconColor: string; defaultIcon: LucideIcon }
> = {
  warn: {
    border: 'border-amber-200 dark:border-amber-900/40',
    iconColor: 'text-amber-600 dark:text-amber-400',
    defaultIcon: AlertCircle,
  },
  error: {
    border: 'border-destructive/30',
    iconColor: 'text-destructive',
    defaultIcon: AlertCircle,
  },
  success: {
    border: 'border-emerald-500/40',
    iconColor: 'text-emerald-600 dark:text-emerald-400',
    defaultIcon: CheckCircle2,
  },
  info: {
    border: 'border-info/30',
    iconColor: 'text-info',
    defaultIcon: Info,
  },
};

const SIZE_STYLES: Record<Size, { wrapper: string; icon: string }> = {
  default: { wrapper: 'gap-3 p-3 text-sm', icon: 'h-4 w-4' },
  compact: { wrapper: 'gap-2 p-2.5 text-xs', icon: 'h-3.5 w-3.5' },
};

export function Banner({
  variant,
  size = 'default',
  icon,
  className,
  children,
}: BannerProps) {
  const v = VARIANT_STYLES[variant];
  const s = SIZE_STYLES[size];
  const Icon = icon ?? v.defaultIcon;
  return (
    <div
      className={cn(
        'flex items-start rounded-md border bg-card',
        v.border,
        s.wrapper,
        className,
      )}
    >
      <Icon className={cn('mt-0.5 shrink-0', s.icon, v.iconColor)} />
      <div className="flex-1">{children}</div>
    </div>
  );
}
